import React, { useCallback, useEffect, useState, useMemo } from "react";
import { api, fmtPKR } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Ban, CheckCircle2, Search, Eye, KeyRound, LogIn, Activity, Zap, Trash2, Crown, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import HardDeleteDialog from "../../components/HardDeleteDialog";

const STATUS_TONE = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  suspended: "bg-red-500/15 text-red-300 border-red-500/30",
  trial: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  deleted: "bg-slate-700/40 text-slate-400 border-slate-600 line-through",
  verification_pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

function HealthBadge({ score }) {
  const tone = score >= 70 ? "emerald" : score >= 40 ? "amber" : "red";
  const cls = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold border tabular ${cls}`}>
      <Activity className="w-3 h-3" /> {score}
    </span>
  );
}

export default function AdminShops() {
  const { startImpersonation } = useAuth();
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [highUsage, setHighUsage] = useState(false);
  const [detail, setDetail] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [limitsTarget, setLimitsTarget] = useState(null);
  const [limMaxCust, setLimMaxCust] = useState("");
  const [limMaxLoans, setLimMaxLoans] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openDelete = (shop) => setDeleteTarget(shop);
  const onDeleted = () => { setDeleteTarget(null); load(); };

  const load = useCallback(
    () => api.get("/admin/shops").then((r) => setRows(r.data)),
    [],
  );
  useEffect(() => {
    load();
    api.get("/admin/plans").then((r) => setPlans(r.data));
  }, [load]);

  const toggleStatus = async (shop) => {
    const endpoint = shop.status === "suspended" ? "activate" : "suspend";
    try {
      await api.post(`/admin/shops/${shop.id}/${endpoint}`);
      toast.success(`Shop ${endpoint === "activate" ? "activated" : "suspended"}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const changePlan = async (shopId, plan_id) => {
    try {
      await api.put(`/admin/shops/${shopId}`, { plan_id });
      toast.success("Plan updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const viewDetail = async (shopId) => {
    const { data } = await api.get(`/admin/shops/${shopId}`);
    setDetail(data);
  };

  const loginAs = async (shop) => {
    if (!confirm(`Enter ADMIN MODE as "${shop.name}"? Every action will be logged.`)) return;
    try {
      await startImpersonation(shop.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/shops/${resetTarget.id}/reset-password`, {
        new_password: newPwd,
      });
      toast.success(`Password reset for ${data.owner_email}`);
      setResetTarget(null);
      setNewPwd("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openLimits = (shop) => {
    setLimitsTarget(shop);
    const cl = shop.custom_limits || {};
    setLimMaxCust(cl.max_customers != null ? String(cl.max_customers) : "");
    setLimMaxLoans(cl.max_active_loans != null ? String(cl.max_active_loans) : "");
  };

  const saveLimits = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        max_customers: limMaxCust === "" ? null : Number(limMaxCust),
        max_active_loans: limMaxLoans === "" ? null : Number(limMaxLoans),
      };
      await api.put(`/admin/shops/${limitsTarget.id}/limits`, payload);
      toast.success("Limits updated");
      setLimitsTarget(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const filtered = useMemo(() => {
    return rows.filter((s) => {
      if (statusFilter === "founding") {
        if (!s.is_founding_member) return false;
      } else if (statusFilter === "expired") {
        const exp = s.trial_ends ? new Date(s.trial_ends) : null;
        if (!exp || exp > new Date()) return false;
      } else if (statusFilter !== "all" && s.status !== statusFilter) {
        return false;
      }
      if (planFilter !== "all" && s.plan_id !== planFilter) return false;
      if (highUsage && (s.customers_count || 0) < 10 && (s.active_loans_count || 0) < 5) return false;
      if (q) {
        const hay = [
          s.name, s.owner_email, s.owner_email_resolved, s.owner_mobile,
          s.distributor_name, s.plan_id,
          s.is_founding_member ? `founding founder ${s.founder_no}` : "",
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, q, statusFilter, planFilter, highUsage]);

  // Subscription actions
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDays, setExtendDays] = useState("30");
  const [extendNotes, setExtendNotes] = useState("");

  const submitExtend = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/shops/${extendTarget.id}/extend-subscription`, {
        days: Number(extendDays),
        notes: extendNotes || undefined,
      });
      toast.success(`Extended by ${extendDays} day(s)`);
      setExtendTarget(null); setExtendDays("30"); setExtendNotes("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  // Feb 30 2026 FINAL — Founding Member plan retired. Grant action removed
  // from the admin UI; the endpoint returns HTTP 410 if called directly.
  // Historical founding members (already flagged in the DB) still render
  // their #<founder_no> badge for audit clarity.
  const grantFounding = async (_s) => {
    toast.error("Founding Member plan is retired. Please use Monthly (PKR 1,500) or Yearly (PKR 15,000).");
  };

  const daysLeft = (iso_str) => {
    if (!iso_str) return null;
    const ms = new Date(iso_str).getTime() - Date.now();
    return Math.ceil(ms / 86400000);
  };

  return (
    <div className="space-y-6" data-testid="admin-shops-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">Shops</h1>
        <p className="text-slate-400 mt-1">Filter, control, monitor every shop on the platform.</p>
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by shop, owner email, mobile, distributor, plan"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-500"
              data-testid="admin-shops-search"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 flex-wrap">
              {["all", "active", "expired", "suspended"].map((f) => (
                <button key={f} onClick={() => setStatusFilter(f)} data-testid={`admin-shops-status-${f}`}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold border capitalize ${statusFilter === f ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                  {f}
                </button>
              ))}
            </div>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="h-8 w-32 bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-shops-plan-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plans</SelectItem>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={() => setHighUsage((v) => !v)} data-testid="admin-shops-high-usage"
              className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${highUsage ? "bg-emerald-500 border-emerald-500 text-slate-900" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
              High usage
            </button>
          </div>
          <div className="text-sm text-slate-500 ml-auto">{filtered.length} of {rows.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Shop</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Plan</th>
                <th className="text-center px-4 py-3 font-semibold">Founding</th>
                <th className="text-left px-4 py-3 font-semibold">Expires</th>
                <th className="text-left px-4 py-3 font-semibold">Distributor</th>
                <th className="text-center px-4 py-3 font-semibold">Health</th>
                <th className="text-right px-4 py-3 font-semibold">Cust.</th>
                <th className="text-right px-4 py-3 font-semibold">Loans</th>
                <th className="text-right px-4 py-3 font-semibold">Collected</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const dl = daysLeft(s.trial_ends);
                const expCls = dl == null ? "text-slate-500"
                  : dl < 0 ? "text-red-300 font-bold"
                  : dl <= 7 ? "text-amber-300 font-semibold"
                  : "text-slate-300";
                return (
                <tr key={s.id} className={`border-t border-slate-800 hover:bg-slate-800/40 ${s.status === "deleted" ? "opacity-60" : ""}`} data-testid={`admin-shop-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-white">
                    {s.name}
                    {s.owner_mobile && <div className="text-[10px] tabular text-slate-500 mt-0.5">{s.owner_mobile}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-400 tabular text-xs">{s.owner_email_resolved || s.owner_email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${STATUS_TONE[s.status] || STATUS_TONE.active}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={s.plan_id} onValueChange={(v) => changePlan(s.id, v)} disabled={s.status === "deleted"}>
                      <SelectTrigger className="h-8 w-32 bg-slate-900 border-slate-700 text-slate-200" data-testid={`admin-plan-select-${s.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {plans.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.is_founding_member ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/40 px-2 py-0.5 text-xs font-bold" data-testid={`admin-shop-founder-${s.id}`}>
                        <Crown className="w-3 h-3" />#{s.founder_no}
                      </span>
                    ) : (
                      <span className="text-slate-600 text-xs">—</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 tabular text-xs ${expCls}`}>
                    {s.trial_ends ? (
                      <>
                        <div>{String(s.trial_ends).slice(0, 10)}</div>
                        <div className="text-[10px]">{dl < 0 ? `${Math.abs(dl)}d overdue` : `${dl}d left`}</div>
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{s.distributor_name || "—"}</td>
                  <td className="px-4 py-3 text-center"><HealthBadge score={s.health_score || 0} /></td>
                  <td className="px-4 py-3 text-right tabular">{s.customers_count}</td>
                  <td className="px-4 py-3 text-right tabular">{s.active_loans_count}</td>
                  <td className="px-4 py-3 text-right tabular text-emerald-300">{fmtPKR(s.collected_total)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      <Button size="sm" variant="ghost" onClick={() => viewDetail(s.id)} className="text-slate-300 hover:text-white" title="View" data-testid={`admin-shop-view-${s.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {s.status !== "deleted" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setExtendTarget(s)} className="text-emerald-300 hover:text-emerald-200" title="Extend subscription" data-testid={`admin-shop-extend-${s.id}`}>
                            <CalendarPlus className="w-4 h-4" />
                          </Button>
                          {/* Grant Founding button retired (Feb 30 2026 FINAL) */}
                          <Button size="sm" variant="ghost" onClick={() => loginAs(s)} className="text-amber-300 hover:text-amber-200" title="Login as" data-testid={`admin-shop-impersonate-${s.id}`}>
                            <LogIn className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setResetTarget(s)} className="text-sky-300 hover:text-sky-200" title="Reset password" data-testid={`admin-shop-reset-${s.id}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openLimits(s)} className="text-emerald-300 hover:text-emerald-200" title="Override plan limits" data-testid={`admin-shop-limits-${s.id}`}>
                            <Zap className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {s.status === "suspended" ? (
                        <Button size="sm" onClick={() => toggleStatus(s)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-shop-activate-${s.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Activate
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={() => toggleStatus(s)} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`admin-shop-suspend-${s.id}`}>
                            <Ban className="w-4 h-4 mr-1" /> Suspend
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openDelete(s)} className="text-red-400 hover:text-red-200" title="Delete shop permanently" data-testid={`admin-shop-delete-${s.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-slate-500">No shops found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#151A23] text-slate-200 border border-slate-800" data-testid="admin-shop-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{detail?.shop?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><div className="text-xs text-slate-500 uppercase">Status</div><div className="text-lg font-display font-bold">{detail.shop.status}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Plan</div><div className="text-lg font-display font-bold">{detail.shop.plan_id}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Owner</div><div className="text-sm tabular">{detail.shop.owner_email}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Created</div><div className="text-sm tabular">{detail.shop.created_at?.slice(0, 10)}</div></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Customers</div>
                  <div className="font-display text-xl font-bold tabular">{detail.customers_count}</div>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Loans</div>
                  <div className="font-display text-xl font-bold tabular">{detail.loans_count}</div>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Payments</div>
                  <div className="font-display text-xl font-bold tabular">{detail.payments_count}</div>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Collected</div>
                  <div className="font-display text-xl font-bold tabular">{fmtPKR(detail.total_collected)}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Users</div>
                <div className="space-y-1">
                  {detail.users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between bg-slate-800/40 rounded px-3 py-2 text-sm">
                      <span>{u.owner_name} <span className="text-slate-500 text-xs">· {u.email}</span></span>
                      <span className="text-xs text-slate-400 uppercase">{u.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="max-w-md bg-[#151A23] text-slate-200 border border-slate-800" data-testid="admin-reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Reset password · {resetTarget?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <div className="text-sm text-slate-400">
              New password for shop owner. Logged in activity log.
            </div>
            <div className="space-y-1.5">
              <Label>New password (min 6 chars)</Label>
              <Input type="password" required minLength={6} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-reset-input" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="admin-reset-submit">Reset password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Limits override dialog */}
      <Dialog open={!!limitsTarget} onOpenChange={(v) => !v && setLimitsTarget(null)}>
        <DialogContent className="max-w-md bg-[#151A23] text-slate-200 border border-slate-800" data-testid="admin-limits-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Override limits · {limitsTarget?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveLimits} className="space-y-4">
            <div className="text-xs text-slate-400">
              Leave blank to fall back to the plan default. Set 0 to hard-block.
            </div>
            <div className="space-y-1.5">
              <Label>Max customers</Label>
              <Input type="number" min={0} value={limMaxCust} onChange={(e) => setLimMaxCust(e.target.value)} placeholder="(plan default)" className="bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-limits-customers" />
            </div>
            <div className="space-y-1.5">
              <Label>Max active loans</Label>
              <Input type="number" min={0} value={limMaxLoans} onChange={(e) => setLimMaxLoans(e.target.value)} placeholder="(plan default)" className="bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-limits-loans" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-limits-submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Extend Subscription dialog */}
      <Dialog open={!!extendTarget} onOpenChange={(v) => !v && setExtendTarget(null)}>
        <DialogContent className="max-w-md bg-[#151A23] text-slate-200 border border-slate-800" data-testid="admin-extend-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-emerald-400" /> Extend Subscription
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitExtend} className="space-y-4 mt-2">
            <div className="text-sm text-slate-400">
              Shop: <span className="text-white font-semibold">{extendTarget?.name}</span>
              {extendTarget?.trial_ends && (
                <div className="text-xs">Current expiry: <span className="tabular">{extendTarget.trial_ends.slice(0, 10)}</span></div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Days to add</Label>
              <div className="flex gap-1.5 flex-wrap">
                {[7, 15, 30, 60, 90, 180, 365].map((d) => (
                  <button type="button" key={d} onClick={() => setExtendDays(String(d))}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${extendDays === String(d) ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}
                    data-testid={`admin-extend-preset-${d}`}>
                    {d}d
                  </button>
                ))}
              </div>
              <Input type="number" min={1} max={3650} value={extendDays} onChange={(e) => setExtendDays(e.target.value)} required className="bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-extend-days" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Input value={extendNotes} onChange={(e) => setExtendNotes(e.target.value)} placeholder="Goodwill, support ticket, etc." className="bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-extend-notes" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-extend-submit">Extend</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hard delete dialog */}
      <HardDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Shop"
        targetLabel={deleteTarget?.name}
        endpoint={deleteTarget ? `/admin/shops/${deleteTarget.id}` : ""}
        previewEndpoint={deleteTarget ? `/admin/shops/${deleteTarget.id}/deletion-preview` : ""}
        description="All customers, loans, payments, agents, and uploaded files will be erased. Subscription history & commission records are preserved for audit."
        onDeleted={onDeleted}
      />
    </div>
  );
}
